import { useEffect, useState } from 'react'

export type PrimaryBookButtonSlotType = 'primary' | 'normal'

type UsePrimaryBookButtonSelectionParams = {
  isEnabled: boolean
  nextOpeningSlot: ProviderSlot | null | undefined
  providerDetails: Provider
  onBook: (
    selectedSlot: SelectedSlot,
    slotType: PrimaryBookButtonSlotType
  ) => void
}

type UsePrimaryBookButtonSelectionReturn = {
  effectiveSlot: SelectedSlot | null
  isSlotSelected: boolean
  /** True from the confirm click until the booking redirect navigates away. */
  isConfirming: boolean
  onSlotClick: (selectedSlot: SelectedSlot) => void
  onConfirm: () => void
  resetSelectedSlot: () => void
}

const usePrimaryBookButtonSelection = ({
  isEnabled,
  nextOpeningSlot,
  providerDetails,
  onBook,
}: UsePrimaryBookButtonSelectionParams): UsePrimaryBookButtonSelectionReturn => {
  const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null)
  const [isConfirming, setIsConfirming] = useState(false)

  useEffect(() => {
    // Mobile browsers restore this page from the back-forward cache after the
    // login redirect instead of remounting it, so isConfirming is still stuck
    // true from the confirm click that navigated away. Clear it on restore.
    const handlePageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setIsConfirming(false)
      }
    }

    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  const effectiveSlot: SelectedSlot | null = isEnabled
    ? selectedSlot ||
      (nextOpeningSlot
        ? { slot: nextOpeningSlot, provider: providerDetails }
        : null)
    : null

  const onSlotClick = (slot: SelectedSlot) => {
    setSelectedSlot(slot)
    setIsConfirming(false)
  }

  const onConfirm = () => {
    if (!effectiveSlot) {
      return
    }

    setIsConfirming(true)
    onBook(effectiveSlot, selectedSlot ? 'normal' : 'primary')
  }

  const resetSelectedSlot = () => {
    setSelectedSlot(null)
  }

  return {
    effectiveSlot,
    isSlotSelected: !!selectedSlot,
    isConfirming,
    onSlotClick,
    onConfirm,
    resetSelectedSlot,
  }
}

export default usePrimaryBookButtonSelection
